import { getFragmentData, gql } from '@/__generated__';
import { HeroItemFragment } from '@/__generated__/graphql';
import { ENTRY_BASE_FRAGMENT } from '@/gql/fragments/entries.gql';
import {
  EVENT_CARD_FRAGMENT,
  PAGE_OVERVIEW_CARD_FRAGMENT,
  PAGE_STANDARD_CARD_FRAGMENT,
} from '@/gql/fragments/entryCards.gql';
import { parseLinkField } from '@/lib/parsers/common';
import { parseEventTimeFragment } from '@/lib/parsers/common/parseEventTime';
import { fmtEventTime } from '@/lib/utils/format';
import { excludeEmptyTypedElements, firstNonNullable, parseUri } from '@liquorice/utils';
import { useTranslations } from 'next-intl';
import Image, { MaybeImage } from '../Image';
import Box from '../ui/Box';
import Btn from '../ui/Btn';
import Container from '../ui/Container';
import Flex from '../ui/Flex';
import Txt from '../ui/Txt';
import VideoBackground from '../Video/VideoBackground';
import * as styles from './HeroItem.css';

export const HERO_ITEM_FRAGMENT = gql(`
  fragment heroItem on heroItem_Entry {
    id
    heroReference {
      ...eventCard
      ...pageStandardCard
      ...pageOverviewCard
      ...entryBase
    }
    title
    content: htmlContentSimple
    linkField {
      ...linkField
    }
    imageSingle {
      ...image
    }
    videoUrl
  }
`);

type HeroItemProps = {
  item: HeroItemFragment;
};

const HeroItem = ({ item }: HeroItemProps) => {
  const {
    title: overrideTitle,
    content: overrideContent,
    imageSingle: overrideImage,
    heroReference,
    videoUrl: overrideVideoUrl,
    linkField: overrideLinkField,
  } = item;

  const reference = firstNonNullable(excludeEmptyTypedElements(heroReference));

  const { uri } = getFragmentData(ENTRY_BASE_FRAGMENT, reference) ?? {};

  let eventTime: string | undefined | null = undefined;
  let title: string | undefined | null = undefined;
  let description: string | undefined | null = undefined;
  let image: MaybeImage | undefined = undefined;
  let videoUrl: string | undefined | null = undefined;

  const maybeLink = parseLinkField(overrideLinkField);

  if (reference?.__typename === 'event_Entry') {
    const event = getFragmentData(EVENT_CARD_FRAGMENT, reference);
    eventTime = fmtEventTime(parseEventTimeFragment(event.eventTime));
    title = overrideTitle || event.entryTitle;
    description = overrideContent || event.entrySummary;
    image = overrideImage.length > 0 ? overrideImage : event.entryImage;
    videoUrl = overrideVideoUrl || event.videoUrl;
  }

  if (reference?.__typename === 'standard_Entry') {
    const page = getFragmentData(PAGE_STANDARD_CARD_FRAGMENT, reference);
    title = overrideTitle || page.entryTitle;
    description = overrideContent || page.entrySummary;
    image = overrideImage.length > 0 ? overrideImage : page.entryImage;
    videoUrl = overrideVideoUrl;
  }

  if (reference?.__typename === 'overview_Entry') {
    const page = getFragmentData(PAGE_OVERVIEW_CARD_FRAGMENT, reference);
    title = overrideTitle || page.entryTitle;
    description = overrideContent || page.entrySummary;
    image = overrideImage.length > 0 ? overrideImage : page.entryImage;
    videoUrl = overrideVideoUrl;
  }

  const href = parseUri(uri);
  const linkProps = {
    ...maybeLink,
    href: maybeLink?.href || href,
  };
  const t = useTranslations('actions');

  return (
    <Container className={styles.root}>
      <Box className={styles.imageWrapper}>
        {videoUrl ? (
          <VideoBackground videoUrl={videoUrl} />
        ) : (
          <Image alt="page-image" priority data={image} fixedRatio />
        )}
      </Box>
      <Flex rowGap="md">
        {title && (
          <Txt variant="h1" uppercase noMargin color="white">
            {title}
          </Txt>
        )}
        <Flex>
          {description && (
            <Txt noMargin html color="white">
              {description}
            </Txt>
          )}
          {eventTime && (
            <Txt noMargin color="white" uppercase weight="medium">
              {eventTime}
            </Txt>
          )}
        </Flex>
        {linkProps.href && (
          <Flex direction="row">
            <Btn
              color="white"
              variant="outlined"
              size="large"
              uppercase
              weight="semibold"
              {...linkProps}
              className={styles.cta}>
              {linkProps.children ?? t('showMore')}
            </Btn>
          </Flex>
        )}
      </Flex>
    </Container>
  );
};

export default HeroItem;
